import { FC, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next"; // Import useTranslation

import useExperimentContainer from "./hooks/useExperimentContainer";

import { ConfirmationModal } from "components/common/modals/ConfirmationModal";
import { Loader } from "components/common/Loader";
import { ExperimentInstructions } from "./components/ExperimentInstructions";
import { ExperimentProducts } from "./components/ExperimentProducts";
import { ExperimentTasting } from "./components/ExperimentTasting";

const Experiment: FC = () => {
  const { t } = useTranslation(); // Initialize translation

  const {
    experiment,
    isJoiningToTheExperiment,
    loading,
    activePage,
    products,
    isExperimentLeaveModalOpen,
    questions,
    answers,
    isNeedHelp,
    canStartSession,
    questionsSection,
    request,
    errorToast,
    redirectToBack,
    handlePageChange,
    getNextProducts,
    handleLeaveModalOpen,
    handleLeaveModalClose,
    handleExperimentLeave,
    handleAnswersChange,
    handleAnswersBtnClick,
    handleHelpAsk,
    handleHelpCancel,
    handleQuestionsSectionPrevChange,
    handleToastClose,
    tasterData,
    postTasterStation,
  } = useExperimentContainer();

  const [tasterStation, setTasterStation] = useState<string | null>(
    tasterData.station ?? ""
  );
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      const currentTaster = experiment?.tasters?.find(
        (taster) => taster.id === tasterData.id
      );
      if (currentTaster) {
        setTasterStation(currentTaster.station ?? "");
        tasterData.station = currentTaster.station;
        firstRender.current = false;
      }
    }
  }, [experiment, tasterData]);

  useEffect(() => {
    if (!loading && !experiment) {
      redirectToBack();
    }
  }, [experiment, loading, redirectToBack]);

  const experimentPages = useMemo(
    () => ({
      intro: (
        <ExperimentInstructions
          experimentName={experiment?.name as string}
          onStartSession={getNextProducts}
          canStartSession={canStartSession}
          error={errorToast}
          handleToastClose={handleToastClose}
          tasterStation={tasterStation}
          onTasterStationChange={(newValue: string) => {
            postTasterStation(newValue);
            setTasterStation(newValue);
          }}
          request={request}
        />
      ),
      products: (
        <ExperimentProducts
          experimentName={experiment?.name as string}
          products={products}
          onNextStep={handlePageChange}
          isExperimentLeaveModalOpen={isExperimentLeaveModalOpen}
          onExperimentLeaveModalOpen={handleLeaveModalOpen}
          onExperimentLeaveModalClose={handleLeaveModalClose}
          onExperimentLeave={handleExperimentLeave}
          isNeedHelp={isNeedHelp}
          onNeedHelpCancel={handleHelpCancel}
          onHelpAsk={handleHelpAsk}
          error={errorToast}
          handleToastClose={handleToastClose}
        />
      ),
      tasting: (
        <ExperimentTasting
          experimentName={experiment?.name as string}
          isExperimentLeaveModalOpen={isExperimentLeaveModalOpen}
          onExperimentLeaveModalOpen={handleLeaveModalOpen}
          onExperimentLeaveModalClose={handleLeaveModalClose}
          onExperimentLeave={handleExperimentLeave}
          instructions={questions?.[questionsSection - 1].instructions}
          questions={questions?.[questionsSection - 1].questions || []}
          currentQuestionsSection={questionsSection}
          questionsSectionCount={questions?.length}
          answers={answers}
          onAnswerChange={handleAnswersChange}
          onSubmit={handleAnswersBtnClick}
          isNeedHelp={isNeedHelp}
          onNeedHelpCancel={handleHelpCancel}
          onHelpAsk={handleHelpAsk}
          handleQuestionsSectionPrevChange={handleQuestionsSectionPrevChange}
          questionsInSectionCount={
            questions?.[questionsSection - 1].questions.length
          }
          error={errorToast}
          handleToastClose={handleToastClose}
        />
      ),
      waiting: (
        <ConfirmationModal
          open
          type="info"
          title={t("answers_sent_title")}
          text={t("answers_sent_text")}
        />
      ),
      finish: (
        <ConfirmationModal
          open
          type="success"
          title={t("experiment_completed_title")}
          text={t("experiment_completed_text")}
          submitBtnText={t("go_home")}
          onSubmit={redirectToBack}
        />
      ),
      noPairs: (
        <ConfirmationModal
          open
          type="success"
          title={t("no_more_pairs_title")}
          text={t("no_more_pairs_text")}
        />
      ),
      kicked: (
        <ConfirmationModal
          open
          type="warning"
          title={t("kicked_from_experiment_title")}
          text={t("kicked_from_experiment_text")}
          submitBtnText={t("go_home")}
          onSubmit={redirectToBack}
        />
      ),
      left: (
        <ConfirmationModal
          open
          type="warning"
          title={t("left_experiment_title")}
          text={t("left_experiment_text")}
          submitBtnText={t("go_home")}
          onSubmit={redirectToBack}
        />
      ),
    }),
    [
      answers,
      canStartSession,
      errorToast,
      experiment?.name,
      getNextProducts,
      tasterStation,
      postTasterStation,
      request,
      handleAnswersBtnClick,
      handleAnswersChange,
      handleExperimentLeave,
      handleHelpAsk,
      handleHelpCancel,
      handleLeaveModalClose,
      handleLeaveModalOpen,
      handlePageChange,
      handleQuestionsSectionPrevChange,
      handleToastClose,
      isExperimentLeaveModalOpen,
      isNeedHelp,
      products,
      questions,
      questionsSection,
      redirectToBack,
      t,
    ]
  );

  if (!loading && !experiment) {
    return null;
  }

  if (loading) {
    return (
      <Loader
        position="absolute"
        transform="translate(-50%, -50%)"
        top="50%"
        left="50%"
      />
    );
  }

  return isJoiningToTheExperiment ? (
    experimentPages[activePage as keyof typeof experimentPages]
  ) : (
    <ConfirmationModal
      open
      type="warning"
      title={t("not_joined_title")}
      text={t("not_joined_text")}
      submitBtnText={t("go_back")}
      onSubmit={redirectToBack}
    />
  );
};

export default Experiment;
